import styled from '@emotion/styled';
import Close from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  DialogTitle,
  FormControl,
  IconButton,
  TextField,
} from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import React, { useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useForm } from 'react-hook-form';

import { WarehouseTransferReceiptForm } from './WarehouseTransferReceiptForm';

export const OperationVoucherType = Object.freeze({
  NO_VOUCHER: 0,
  DOMESTIC_PURCHASE: 1,
  COMMERCIAL_DOMESTIC_PURCHASE: 2,
  WAREHOUSE_TRANSFER_RECEIPT: 3,
});

const STEPS = Object.freeze({
  SELECT: 0,
  VOUCHER_FORM: 1,
  DETAILS: 2,
});

const voucherTypeOptions = [
  {
    type: OperationVoucherType.DOMESTIC_PURCHASE,
    label: 'خرید داخلی',
    enabled: false,
  },
  {
    type: OperationVoucherType.COMMERCIAL_DOMESTIC_PURCHASE,
    label: 'خرید داخلی (بازرگانی)',
    enabled: false,
  },
  {
    type: OperationVoucherType.WAREHOUSE_TRANSFER_RECEIPT,
    label: 'رسید انتقال بین انبار ها',
    enabled: true,
  },
  {
    type: OperationVoucherType.NO_VOUCHER,
    label: 'بدون سند',
    enabled: true,
  },
];

const stepTitles = {
  [STEPS.SELECT]: 'بخش مورد نظر را انتخاب کنید',
  [STEPS.VOUCHER_FORM]: 'شناسه حواله را وارد کنید',
  [STEPS.DETAILS]: 'اطلاعات مورد نظر را وارد کنید',
};

const detailsDefaultValues = {
  title: '',
  description: '',
};

const toIntOrNull = value => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : null;
};

const EntryOperationInsertModal = ({
  open,
  handleClose,
  insertLoading,
  onInsert,
}) => {
  const [{ operationSettings }] = useCookies(['operationSettings']);
  const [step, setStep] = useState(STEPS.SELECT);
  const [voucherType, setVoucherType] = useState();
  const [voucherData, setVoucherData] = useState({});

  const {
    handleSubmit,
    reset,
    register,
    formState: { errors, isSubmitted },
  } = useForm({ defaultValues: detailsDefaultValues });

  useEffect(() => {
    if (!open) {
      setStep(STEPS.SELECT);
      setVoucherType(undefined);
      setVoucherData({});
      reset(detailsDefaultValues);
    }
  }, [open, reset]);

  const handleSelectVoucherType = type => {
    setVoucherType(type);
    setVoucherData({});

    if (type === OperationVoucherType.WAREHOUSE_TRANSFER_RECEIPT) {
      setStep(STEPS.VOUCHER_FORM);
      return;
    }

    setStep(STEPS.DETAILS);
  };

  const handleVoucherFormSubmit = data => {
    setVoucherData(prev => ({ ...prev, ...data }));
    setStep(STEPS.DETAILS);
  };

  const handleDetailsSubmit = data => {
    onInsert?.({
      zoneGuid: operationSettings?.defaultZone?.key,
      title: data.title?.trim() ?? '',
      description: data.description?.trim() ?? '',
      voucherType: Number(voucherType),
      counterpartID: toIntOrNull(voucherData.counterpartID),
      voucherID: toIntOrNull(voucherData.voucherID),
      invoiceNumber: toIntOrNull(voucherData.invoiceNumber),
    });
  };

  const handleBack = () => {
    if (step === STEPS.DETAILS) {
      if (voucherType === OperationVoucherType.WAREHOUSE_TRANSFER_RECEIPT) {
        setStep(STEPS.VOUCHER_FORM);
        return;
      }
      setStep(STEPS.SELECT);
      setVoucherType(undefined);
      return;
    }

    if (step === STEPS.VOUCHER_FORM) {
      setStep(STEPS.SELECT);
      setVoucherType(undefined);
      setVoucherData({});
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      sx={{ direction: 'rtl', minWidth: '360px' }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontSize: 16,
        }}
      >
        {stepTitles[step]}
        <IconButton onClick={handleClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {step === STEPS.SELECT ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              pt: 2,
              minWidth: 320,
            }}
          >
            {voucherTypeOptions
              .filter(option => option.enabled)
              .map(option => (
                <OptionButton
                  key={option.type}
                  fullWidth
                  onClick={() => handleSelectVoucherType(option.type)}
                >
                  {option.label}
                </OptionButton>
              ))}
          </Box>
        ) : null}

        {step === STEPS.VOUCHER_FORM &&
        voucherType === OperationVoucherType.WAREHOUSE_TRANSFER_RECEIPT ? (
          <WarehouseTransferReceiptForm
            onSubmit={handleVoucherFormSubmit}
            loading={insertLoading}
          />
        ) : null}

        {step === STEPS.DETAILS ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              pt: 1,
              minWidth: 300,
            }}
          >
            <FormControl variant="standard" sx={{ m: 2, minWidth: 280 }}>
              <TextField
                autoFocus
                label="عنوان"
                variant="standard"
                {...register('title', { required: 'عنوان الزامی است' })}
                error={isSubmitted ? !!errors.title : false}
                helperText={isSubmitted ? errors.title?.message : null}
              />
            </FormControl>

            <FormControl variant="standard" sx={{ m: 2, minWidth: 280 }}>
              <TextField
                label="توضیحات"
                variant="outlined"
                multiline
                minRows={3}
                {...register('description')}
              />
            </FormControl>
          </Box>
        ) : null}
      </DialogContent>

      {step === STEPS.DETAILS ? (
        <DialogActions sx={{ gap: 1, padding: 3 }}>
          <Button
            onClick={handleBack}
            variant="contained"
            disabled={insertLoading}
          >
            بازگشت
          </Button>
          <LoadingButton
            color="warning"
            variant="contained"
            onClick={handleSubmit(handleDetailsSubmit)}
            loading={insertLoading}
          >
            ثبت عملیات
          </LoadingButton>
        </DialogActions>
      ) : null}
    </Dialog>
  );
};

export default EntryOperationInsertModal;

const OptionButton = styled(Button)`
  background-color: #e8e8e8;
  color: #333;
  border-radius: 8px;
  padding: 12px 16px;
  font-weight: 500;
  box-shadow: none;

  &:hover {
    background-color: #dcdcdc;
    box-shadow: none;
  }
`;
